import { cn } from "@app/lib/utils";
import {
  investorResources,
  resolveResourceFile,
} from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { Download } from "lucide-react";
import type { ReactNode } from "react";

const PENDING_HREF = "#";

type ResourceActionProps = {
  href: string;
  variant: "outline" | "solid";
  label: string;
  resourceTitle: string;
  /**
   * Saves the file instead of opening it. Honoured for the same-origin
   * `/pdf/*` documents; browsers ignore it on the cross-origin Annual Report,
   * which falls back to opening in a new tab.
   */
  download?: boolean;
  children?: ReactNode;
};

/**
 * Renders as a link once a real document URL exists, and as a disabled control
 * while the content sheet's "Links to be provided" placeholder is still in
 * place — so the section never ships a dead `#` anchor.
 */
function ResourceAction({
  href,
  variant,
  label,
  resourceTitle,
  download,
  children,
}: ResourceActionProps) {
  const classes = cn(
    "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors sm:text-sm",
    variant === "solid"
      ? "bg-secondary text-white hover:bg-secondary/90"
      : "border border-mercury text-marine hover:border-secondary hover:text-secondary",
  );

  const content = (
    <>
      {label}
      {children}
      <span className="sr-only"> — {resourceTitle}</span>
    </>
  );

  if (href === PENDING_HREF) {
    return (
      <button
        type="button"
        disabled
        title={trans("investorResources.linkPending")}
        className={cn(classes, "cursor-not-allowed opacity-45")}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      href={href}
      download={download}
      target="_blank"
      rel="noopener noreferrer"
      className={classes}
    >
      {content}
    </a>
  );
}

/** Investor Resources — Section 08 of the approved IR content sheet. */
export function InvestorResources() {
  return (
    <section
      aria-labelledby="investor-resources-title"
      className="py-14 lg:py-16"
    >
      <div className="container flex flex-col gap-8">
        <h2
          id="investor-resources-title"
          className="text-2xl font-bold text-marine sm:text-3xl"
        >
          {trans("investorResources.title")}
        </h2>

        <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {investorResources.map(({ key, title, meta, cover, file }) => {
            const resourceTitle = trans(title);
            const href = resolveResourceFile(file);

            return (
              <li
                key={key}
                className="flex flex-col rounded-2xl border border-mercury bg-white p-5 shadow-leadership-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-bold leading-snug text-marine">
                    {resourceTitle}
                  </h3>
                  <span className="shrink-0 pt-0.5 text-[0.6875rem] font-medium uppercase tracking-wider text-marine/45">
                    {trans(meta)}
                  </span>
                </div>

                {/* Cover preview. TODO: swap for the approved document
                    mock-ups (cover + inner spread) when they are delivered. */}
                <div className="mt-5 flex flex-1 items-center justify-center rounded-xl bg-[#eef2f9]">
                  <img
                    src={cover}
                    alt=""
                    width={900}
                    height={1200}
                    loading="lazy"
                    className="relative w-full rounded-md object-cover shadow-lg"
                  />
                </div>

                {/* Both actions point at the same document — View opens it,
                    Download saves it. */}
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <ResourceAction
                    href={href}
                    variant="outline"
                    label={trans("investorResources.view")}
                    resourceTitle={resourceTitle}
                  />
                  <ResourceAction
                    href={href}
                    download
                    variant="solid"
                    label={trans("investorResources.download")}
                    resourceTitle={resourceTitle}
                  >
                    <Download className="size-4" strokeWidth={2} aria-hidden />
                  </ResourceAction>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
